import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
const PAGE_SIZE = 9;
const FALLBACK_IMAGE = "/assets/img/popular/popular-1-2.jpg";
const DEFAULT_PHONE = "971507455838";
const LISTING_IMAGE_VARIANT = "w=520,sharpen=1";

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return baseUrl + pathname;
}

function titleFromSlug(slug) {
  return String(slug || "Offplan")
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function imageWithVariant(image, variant, fallback) {
  if (!image) {
    return fallback;
  }

  if (image.includes("imagedelivery.net") && !image.includes("/w=")) {
    return image.replace(/\/$/, "") + "/" + variant;
  }

  return image;
}

function normalizeImagePath(image) {
  if (!image) {
    return FALLBACK_IMAGE;
  }

  if (/^https?:\/\//i.test(image) || image.startsWith("/")) {
    return image;
  }

  if (image.startsWith("assets/")) {
    return "/" + image;
  }

  return "https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q/" + image.replace(/^\/+/, "");
}

function formatPrice(price) {
  const value = String(price || "").trim();

  if (!value) {
    return "Price on request";
  }

  return /^aed/i.test(value) ? value : "AED " + value;
}

function projectLocation(project) {
  return [project.community, project.sub_community, project.city].filter(Boolean).join(" - ") || "Dubai - UAE";
}

function propertyTypeText(project) {
  if (Array.isArray(project.property_type)) {
    return project.property_type.filter(Boolean).join(", ");
  }

  return project.property_type || project.propertytype || "Off-plan";
}

function offPlanUrl(project) {
  const referenceNumber = project.reference_number || project.referenceNumber || project.property_id || project.id;
  const slug = String(project.url || project.title || "")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)[0];
  const encodedReference = referenceNumber ? encodeURIComponent(referenceNumber) : "";

  if (slug && encodedReference) {
    return "/offplan-details/" + slug + "/" + encodedReference;
  }

  return encodedReference ? "/offplan-details/" + encodedReference : "/offplan-details";
}

function absoluteOffPlanUrl(project) {
  const detailUrl = offPlanUrl(project);

  if (/^https?:\/\//i.test(detailUrl) || typeof window === "undefined") {
    return detailUrl;
  }

  return window.location.origin + detailUrl;
}

function whatsappMessage(project) {
  const projectTitle = project.title || "this off-plan project";
  const referenceNumber = project.reference_number || project.referenceNumber || project.property_id || project.id;
  const messageParts = [
    "Hello Blue Water Real Estate, I am interested in " + projectTitle + ".",
    referenceNumber ? "Reference: " + referenceNumber + "." : "",
    "Project details: " + absoluteOffPlanUrl(project),
    "Please share more information.",
  ];

  return messageParts.filter(Boolean).join(" ");
}

function whatsappUrl(project) {
  const rawPhone = project.agent_whatsapp || project.agent_phone || project.whtsapp || project.whatsapp || DEFAULT_PHONE;
  const cleanPhone = String(rawPhone).replace(/[^\d+]/g, "");
  const message = encodeURIComponent(whatsappMessage(project));

  return "https://api.whatsapp.com/send?phone=" + encodeURIComponent(cleanPhone || DEFAULT_PHONE) + "&text=" + message;
}

function phoneUrl(phone) {
  const cleanPhone = String(phone || DEFAULT_PHONE).replace(/[^\d+]/g, "");
  return "tel:" + cleanPhone;
}

function imageValue(photo) {
  if (!photo) {
    return "";
  }

  if (typeof photo === "string") {
    return photo;
  }

  return photo.image || photo.ImagePath || photo.url || photo.src || "";
}

function uniqueImages(images) {
  return [...new Set(images.filter(Boolean))];
}

function listingImageUrl(image) {
  return imageWithVariant(normalizeImagePath(image), LISTING_IMAGE_VARIANT, FALLBACK_IMAGE);
}

function offPlanImages(project) {
  const photos = Array.isArray(project.photos) ? project.photos : [];
  const photoImages = uniqueImages(photos.map(imageValue).filter(Boolean).map(listingImageUrl));

  if (photoImages.length) {
    return photoImages;
  }

  return [listingImageUrl(project.ImagePath || project.image)];
}

function openImageGallery(event, images) {
  event.preventDefault();

  const galleryItems = images.filter(Boolean).map((src) => ({ src }));
  const $ = window.jQuery || window.$;

  if (galleryItems.length && $?.magnificPopup?.open) {
    $.magnificPopup.open(
      {
        items: galleryItems,
        gallery: {
          enabled: true,
        },
        type: "image",
        mainClass: "mfp-zoom-in",
        removalDelay: 260,
      },
      0
    );
    return;
  }

  if (galleryItems[0]) {
    window.location.href = galleryItems[0].src;
  }
}

function paginationItems(currentPage, totalPages) {
  const start = Math.max(currentPage - 2, 1);
  const end = Math.min(start + 4, totalPages);
  const adjustedStart = Math.max(end - 4, 1);
  const pages = [];

  for (let page = adjustedStart; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
}

function OffPlanCard({ project }) {
  const images = offPlanImages(project);
  const primaryImage = images[0] || FALLBACK_IMAGE;

  return (
    <div className="col-xxl-4 col-xl-4 col-lg-6 col-md-6 filter-item cat3 cat1">
      <div className="popular-list-1">
        <div className="thumb-wrapper">
          <Link to={offPlanUrl(project)} className="th-slider property-listing-image">
            <img src={imageWithVariant(primaryImage, LISTING_IMAGE_VARIANT, FALLBACK_IMAGE)} alt={project.title || "Off-plan project"} />
          </Link>
          <div className="actions-style-2-wrapper">
            <div className="actions style-2">
              <Link to={offPlanUrl(project)} className="icon-btn">
                <i className="fa-solid fa-link" />
              </Link>
              <button type="button" className="icon-btn popular-popup-image" onClick={(event) => openImageGallery(event, images)}>
                <i className="fa-solid fa-camera" />
              </button>
            </div>
          </div>
        </div>
        <div className="property-content">
          <div className="media-body">
            <h3 className="box-title">
              <Link to={offPlanUrl(project)}>{project.title || "Off-plan Project"}</Link>
            </h3>
            <div className="box-text">
              <div className="icon">
                <img src="/assets/img/icon/2.webp" alt="location" />
              </div>
              {projectLocation(project)}
            </div>
          </div>
          <ul className="property-featured">
            <li>
              <div className="icon">
                <i className="fa-solid fa-building" />
              </div>
              {propertyTypeText(project)}
            </li>
            <li>
              <div className="icon">
                <i className="fa-solid fa-calendar-check" />
              </div>
              {project.moveinstatus || "Off Plan"}
            </li>
            <li>
              <div className="icon">
                <i className="fa-solid fa-file-invoice-dollar" />
              </div>
              {project.paymentplan || "Payment Plan"}
            </li>
          </ul>
          <div className="property-bottom">
            <h6 className="box-title">{formatPrice(project.price)}</h6>
            <a className="th-btn sm style3 pill" href={phoneUrl(project.call_number)} aria-label="Call">
              <i className="fa-solid fa-phone" />
            </a>
            <a className="th-btn sm style3 pill" href={whatsappUrl(project)} target="_blank" rel="noreferrer" style={{ padding: 0 }} aria-label="WhatsApp">
              <img src="/assets/img/icon/3.webp" width="38" alt="WhatsApp" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function OffPlanListing() {
  const { developerId, developerSlug } = useParams();
  const [developerName, setDeveloperName] = useState(titleFromSlug(developerSlug));
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  const requestBody = useMemo(
    () => ({
      id: developerId || "",
      page,
      pageSize: PAGE_SIZE,
      sortBy: "id DESC",
    }),
    [developerId, page]
  );

  useEffect(() => {
    setPage(1);
    setDeveloperName(titleFromSlug(developerSlug));
  }, [developerId, developerSlug]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadOffPlanListing() {
      setStatus("loading");
      setError("");

      try {
        const response = await fetch(buildApiUrl("/api/offplanprojectbydeveloper.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Unable to load off-plan projects");
        }

        const payload = await response.json();

        if (payload.result !== 1) {
          throw new Error(payload.message || "Unable to load off-plan projects");
        }

        setDeveloperName(payload.developername || titleFromSlug(developerSlug));
        setProjects(Array.isArray(payload.data) ? payload.data : []);
        setTotal(Number(payload.total) || 0);
        setStatus("ready");
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        setProjects([]);
        setTotal(0);
        setError(err.message || "Unable to load off-plan projects");
        setStatus("error");
      }
    }

    loadOffPlanListing();

    return () => controller.abort();
  }, [developerSlug, requestBody]);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  function goToPage(nextPage) {
    const boundedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setPage(boundedPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <>
      <div className="breadcumb-wrapper" style={{ backgroundImage: "url('/assets/img/egefe.jpg')" }}>
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{developerName === "Offplan" ? "Offplan" : developerName + " Offplan Projects"}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/developers">Developers</Link>
              </li>
              <li>{developerName}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="popular-sec-1 space">
        <div className="container">
          <div className="title-area text-center">
            <h2 className="sec-title">{developerName}</h2>
            {status === "ready" && <p className="sec-text">{total} off-plan projects found</p>}
          </div>

          {status === "loading" && <div className="text-center">Loading off-plan projects...</div>}
          {status === "error" && <div className="text-center">{error}</div>}
          {status === "ready" && projects.length === 0 && <div className="text-center">No off-plan projects found.</div>}
          {status === "ready" && projects.length > 0 && (
            <>
              <div className="row gy-30 justify-content-center filter-active">
                {projects.map((project) => (
                  <OffPlanCard project={project} key={project.id || project.title} />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="property-search-pagination">
                  <button type="button" className="th-btn sm style3 pill" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                    <i className="far fa-arrow-left" />
                  </button>
                  {paginationItems(page, totalPages).map((paginationPage) => (
                    <button
                      type="button"
                      className={"th-btn sm style3 pill" + (paginationPage === page ? " active" : "")}
                      onClick={() => goToPage(paginationPage)}
                      key={paginationPage}
                    >
                      {paginationPage}
                    </button>
                  ))}
                  <button type="button" className="th-btn sm style3 pill" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                    <i className="far fa-arrow-right" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

export default OffPlanListing;
